<template>
  <component :is="props.tooltip && props.tooltip.length ? ZtTooltip : useFragment" :content="props.tooltip" placement="top">
    <component :is="buttonType.type" class="button" :class="getClass()" :data-testid="dataTestid" v-bind="{ ...$attrs, ...buttonType.attrs }">
      <Icon v-if="icon && icon.length && !loading" :name="icon" :size="iconSize" />
      <Icon v-if="loading" name="general.spinner" :size="iconSize" />
      <span v-if="hasText" class="whitespace-nowrap" data-testid="button-text">{{ text }}<slot /></span>
      <Icon v-if="suffixIcon && suffixIcon.length" :name="suffixIcon" :size="iconSize" />
      <BetaBadge v-if="isBeta" class="absolute -top-1 -right-1" size="regular" />
      <NewBadge v-else-if="isNew" class="absolute -top-1 -right-1" size="regular" />
    </component>
  </component>
</template>

<script lang="ts" setup>
import { RouteLocationRaw, RouterLink } from 'vue-router';
import { computed, useSlots } from 'vue';
import { Icon } from './../icon';
import { ZtTooltip } from '../tooltip';
import { BetaBadge } from './../utilities';
import { NewBadge } from './../utilities';
import { useFragment } from '../composables/useFragment';

const slots = useSlots();

interface Props {
  variant?: 'primary' | 'link' | 'ai' | 'ai-purple';
  disabled?: boolean;
  icon?: string;
  suffixIcon?: string;
  to?: RouteLocationRaw;
  text?: string;
  submit?: boolean;
  loading?: boolean;
  tooltip?: string;
  dataTestid?: string;
  size?: 'regular' | 'large';
  iconSize?: 'xs' | 'sm' | 'base' | 'lg';
  isBeta?: boolean;
  isNew?: boolean;
}

const props = withDefaults(defineProps<Props>(), { size: 'regular', variant: 'link', iconSize: 'sm' });
const hasText = computed(() => !!slots.default || (props.text && props.text.length));

const buttonType = computed(() => {
  if (props.submit) {
    return {
      type: 'button',
      attrs: { type: 'submit', disabled: props.disabled },
    };
  }

  if (props.to) {
    return {
      type: RouterLink,
      attrs: { to: props.to },
    };
  }

  return { type: 'a' };
});

const getClass = () => {
  const toReturn: any = {};

  toReturn[props.variant] = true;
  toReturn['disabled'] = props.disabled;
  toReturn['loading'] = props.loading;
  toReturn[props.size] = true;

  return toReturn;
};
</script>

<style>
.button {
  min-block-size: var(--button-block-size, 2.25rem);
  display: var(--button-display, inline-flex);
  align-items: center;
  gap: var(--button-gap, 0.375rem);
  padding: var(--button-padding, 0.125rem 0.5rem);

  background: var(--button-bg, var(--color-primary));
  color: var(--black-copernicus, var(--color-light));

  border-width: var(--button-border-width, var(--stroke-width));
  border-style: var(--button-border-style, solid);
  border-color: var(--button-border-color, transparent);
  border-radius: var(--button-radius, var(--radius-l));

  text-decoration: none;
  font-weight: var(--button-font-weight, 600);
  font-size: var(--button-font-size, 0.75rem);
  letter-spacing: 0.05ch;
  font-family: var(--serif-font-family, sans-serif);
  line-height: 1.1;

  cursor: pointer;
}

.button svg {
  height: var(--button-icon-size, 1.25rem);
  width: auto;
  flex: none;
}

.button:hover {
  background: var(--button-hover-bg, var(--color-primary-darken));
}

.button:active:not(.disabled) {
  transform: scale(99%);
}

.button.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.button.primary {
  --button-bg: var(--blue-500);
  --button-hover-bg: var(--blue-600);
  --button-color: var(--white-copernicus);
}
.button.ai {
  --button-bg: linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%), linear-gradient(90deg, #f5d9b7 3.72%, #e0adff 95.74%);
  --button-hover-bg: linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%), linear-gradient(90deg, #f5d9b7 3.72%, #e0adff 95.74%);
  --button-radius: var(--radius-m);
  --button-border-color: #ebdde2;
}
.dark .button.ai {
  --button-bg: linear-gradient(90deg, rgba(245, 217, 183, 0.3) 3.72%, rgba(224, 173, 255, 0.3) 95.74%);
  --button-hover-bg: linear-gradient(90deg, rgba(245, 217, 183, 0.5) 3.72%, rgba(224, 173, 255, 0.5) 95.74%);
  --button-border-color: rgba(235, 221, 226, 0.4);
  --button-color: var(--white-copernicus);
}
.button.ai-purple {
  --button-bg: linear-gradient(270deg, #9557AF 7.02%, #483473 96.49%);
  --button-hover-bg: linear-gradient(270deg, #9557AF 67.24%, #69519C 96.49%);
  --button-radius: var(--radius-l);
  --button-border-color: #ebdde2;
  color: var(--white-copernicus);
}
.dark .button.ai-purple {
  --button-bg: linear-gradient(270deg, #9557AF 7.02%, #483473 96.49%);
  --button-hover-bg: linear-gradient(270deg, #9557AF 67.24%, #69519C 96.49%);
  --button-border-color: var(--zinc-800);
}
.button.link {
  --button-bg: transparent;
  --button-hover-bg: rgb(226 232 240);
  --button-radius: var(--radius-m);
  --button-border-color: transparent;
}
.dark .button.link {
  --button-hover-bg: rgb(59 73 89);
  --button-color: var(--white-copernicus);
}
</style>
